"use client";

import { useEffect, useState } from "react";
import Dropdown from "./Dropdown";
import { motion } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import { useWindowSize } from "../hooks/useWindowSize";
import { Option } from "./Dropdown";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Checkbox from "@/components/Checkbox";
import Cta from "./Cta";
import { useDebounce } from "../hooks/useDebounce";
import dayjs from "dayjs";
import { TournamentQuery } from "@/types/tournament-query";
import clsx from "clsx";

const GENDERS = [
  { value: null, label: "All" },
  { value: "MALE", label: "Men" },
  { value: "FEMALE", label: "Women" },
];

interface LeaderboardActionBarProps {
  nationalitiesOptions?: Array<{ label: string; value: string | null }>;
  defaultDropdownOption: Option;
  dropdownOptions: Option[];
  isNowPlaying: boolean;
  tournamentQuery: TournamentQuery;
  tournamentName?: string;
  onSelectChange: (i: any) => void;
  onApplyFilters: (i: any) => void;
  isCTPLeaderboard: boolean;
  ctpDefaultDropdownOption: Option;
  ctpDropdownOptions: Option[];
  onCTPSelectChange: (i: any) => void;
  onLongestSelectChange: (i: any) => void;
  startDate: string;
  defaultRoundOption: any;
  roundOptions: Array<{
    id: number | string;
    groupId?: string;
    label: string;
    tQuery: TournamentQuery;
    tQueryKey: string;
    tQueryVariables: any;
  }>;
  isMultiRoundTournament?: boolean;
  longestOptions?: Array<{
    id: number | string;
    groupId?: string;
    label: string;
    tQuery: TournamentQuery;
    tQueryKey: string;
    tQueryVariables: any;
  }>;
}

const LeaderboardActionBar = ({
  nationalitiesOptions,
  defaultDropdownOption,
  dropdownOptions,
  isNowPlaying,
  tournamentQuery,
  tournamentName,
  onSelectChange,
  onApplyFilters,
  isCTPLeaderboard,
  ctpDefaultDropdownOption,
  ctpDropdownOptions,
  onCTPSelectChange,
  startDate,
  defaultRoundOption,
  roundOptions,
  isMultiRoundTournament,
  longestOptions,
  onLongestSelectChange,
}: LeaderboardActionBarProps) => {
  const { windowSize } = useWindowSize();
  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [gender, setGender] = useState<{ value: string | null; label: string }>(
    { value: null, label: "All" }
  );
  const [playNow, setPlayNow] = useState(false);
  const [nationalities, setNationalities] = useState<
    Array<{
      value: string | null;
      label: string;
    }>
  >([{ value: null, label: "All" }]);
  const [ctpOptionValue, setCtpOptionValue] = useState<any>(undefined);
  const [longestOptionValue, setLongestOptionValue] = useState<any>(undefined);

  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    if (htmlElement && bodyElement) {
      if (isOpen) {
        htmlElement.style.overflow = "hidden";
        bodyElement.style.overflow = "hidden";
      } else {
        htmlElement.style.overflow = "";
        bodyElement.style.overflow = "";
      }
    }

    return () => {
      if (htmlElement && bodyElement) {
        htmlElement.style.overflow = "";
        bodyElement.style.overflow = "";
      }
    };
  }, [isOpen]);

  const handlePlayNowChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPlayNow(!playNow);

    onApplyFilters({
      playNow: isChecked || null,
    });

    if (isChecked) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "checkNowPlaying",
        playNow: playNow,
        tournamentName: tournamentName || "",
      });
    }
  };

  const handleGenderChange = ({
    label,
    value,
  }: {
    label: string;
    value: string | null;
  }) => {
    if (gender.label !== label) {
      setGender({ value, label });
    }
  };

  const handleNationalityChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    const isChecked = event.target.checked;

    if (isChecked) {
      setNationalities((prevNationalities: any) => [
        ...prevNationalities,
        {
          value: newValue,
          label: nationalitiesOptions?.find(
            (option) => option?.value === newValue
          )?.label,
        },
      ]);
    } else {
      setNationalities((prevNationalities) =>
        prevNationalities.filter((n) => n.value !== newValue)
      );
    }
  };

  const onClear = () => {
    setSearchText("");
    setPlayNow(false);
    setGender({ value: null, label: "All" });
    setNationalities([{ value: null, label: "All" }]);

    onApplyFilters({
      playNow: null,
      nationality: [],
      searchText: "",
      ...(tournamentQuery !== "longestDrive" && { gender: null }),
    });
    setAppliedFiltersCount(0);
  };

  const onApply = () => {
    onApplyFilters({
      playNow: playNow || null,
      nationality: nationalities
        .filter((i) => i.value !== null)
        ?.map((i) => i.value),
      searchText: "",
      ...(tournamentQuery !== "longestDrive" && { gender: gender.value }),
    });
    setSearchText("");
    setIsOpen(false);

    countAndSetAppliedFilters();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "applyFilter",
      tournamentName: tournamentName || "",
    });
  };

  const countAndSetAppliedFilters = () => {
    let filtersCount = 0;
    const selectedNationalitiesCount = nationalities.filter(
      (n) => n.value !== null
    ).length;

    if (gender.value !== null) {
      filtersCount++;
    }
    filtersCount += selectedNationalitiesCount;
    setAppliedFiltersCount(filtersCount);
  };

  const filterPanelVariants = {
    initial: {
      opacity: 0,
      x: !(windowSize.width < 961) ? "100%" : 0,
      y: windowSize.width < 961 ? "100%" : 0,
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
    },
  };

  const backgroundVariants = {
    initial: {
      display: "none",
      opacity: 0,
    },
    open: {
      display: "block",
      opacity: 0.7,
    },
  };

  const debouncedSearchText = useDebounce(searchText, 300);

  useEffect(() => {
    onApplyFilters({ searchText: debouncedSearchText });

    if (debouncedSearchText) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "search",
        searchText: debouncedSearchText,
        tournamentName: tournamentName || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  const handleSearchInputChange = (event: { target: { value: any } }) => {
    const searchText = event.target.value;
    setSearchText(searchText);
  };

  return (
    <>
      <div
        className={clsx(
          `mb-2.5 grid justify-center
          gap-2.5 md:gap-x-5 
          xl:justify-between xl:items-center xl:grid-flow-col
        `,
          {
            "xl:grid-flow-row 2xl:grid-flow-col":
              isMultiRoundTournament &&
              (isCTPLeaderboard || tournamentQuery === "longestDrive"),
          }
        )}
      >
        <div className="flex w-[330px] md:w-full [&>label]:gap-2.5">
          <Checkbox
            disabled={!isNowPlaying || tournamentQuery !== "leaderboard"}
            label="Now playing"
            isChecked={playNow}
            onChange={handlePlayNowChange}
          />
        </div>
        <div className="flex flex-col gap-2.5 xl:flex-row">
          <div className="w-[330px] flex flex-row gap-2.5">
            <Dropdown
              defaultValue={defaultDropdownOption}
              options={dropdownOptions}
              onChange={(e: any) => {
                setSearchText("");
                onSelectChange(e);
                setPlayNow(false);
                setGender({ value: null, label: "All" });
                setNationalities([{ value: null, label: "All" }]);
                setAppliedFiltersCount(0);
              }}
            />
          </div>
          {isCTPLeaderboard && (
            <div className="flex gap-2.5">
              {isMultiRoundTournament && (
                <Dropdown
                  className="xl:!w-[180px]"
                  defaultValue={defaultRoundOption}
                  options={roundOptions}
                  onChange={(e: any) => {
                    if (roundOptions?.length === 1) return;
                    setCtpOptionValue(ctpDefaultDropdownOption);
                    onCTPSelectChange(e);
                    setSearchText("");
                    setPlayNow(false);
                    setGender({ value: null, label: "All" });
                    setNationalities([{ value: null, label: "All" }]);
                    setAppliedFiltersCount(0);
                  }}
                />
              )}
              <Dropdown
                className="xl:!w-[180px]"
                defaultValue={ctpDefaultDropdownOption}
                options={ctpDropdownOptions}
                value={ctpOptionValue}
                onChange={(e: any) => {
                  onCTPSelectChange(e);
                  setSearchText("");
                  setPlayNow(false);
                  setGender({ value: null, label: "All" });
                  setNationalities([{ value: null, label: "All" }]);
                  setAppliedFiltersCount(0);
                }}
              />
            </div>
          )}
          {isMultiRoundTournament && tournamentQuery === "longestDrive" && (
            <div className="flex gap-2.5">
              <Dropdown
                className="xl:!w-[180px]"
                defaultValue={defaultRoundOption}
                options={roundOptions}
                onChange={(e: any) => {
                  if (roundOptions?.length === 1) return;
                  setLongestOptionValue(longestOptions?.[0]);
                  onLongestSelectChange(e);
                  setSearchText("");
                  setPlayNow(false);
                  setGender({ value: null, label: "All" });
                  setNationalities([{ value: null, label: "All" }]);
                  setAppliedFiltersCount(0);
                }}
              />
              <Dropdown
                className="xl:!w-[180px]"
                defaultValue={longestOptions?.[0]}
                options={longestOptions}
                value={longestOptionValue}
                onChange={(e: any) => {
                  onLongestSelectChange(e);
                  setSearchText("");
                  setPlayNow(false);
                  setGender({ value: null, label: "All" });
                  setNationalities([{ value: null, label: "All" }]);
                  setAppliedFiltersCount(0);
                }}
              />
            </div>
          )}
          <div className="w-[330px] xl:w-[400px] flex gap-2.5">
            <div className="w-[330px] flex-row gap-2.5 items-center h-[45px] text-[15px] outline-none bg-tm-c-gray-800 border-0 text-tm-c-light cursor-pointer rounded-[10px] px-2.5 pl-4 py-2.5 flex justify-between relative lg:w-[330px]">
              <input
                className="border-0 bg-transparent m-0 p-0 text-inherit text-[15px] w-full focus:outline-none"
                type="search"
                placeholder="Search for player"
                value={searchText}
                onChange={handleSearchInputChange}
              />
              <div className="relative h-full [&>svg]:flex [&>svg]:m-auto [&>svg]:h-full">
                <SearchIcon fontSize="medium" />
              </div>
            </div>
            <div className="flex">
              <div
                className="flex items-center h-[45px] text-[15px] outline-none bg-tm-c-gray-800 border-0 text-tm-c-light cursor-pointer rounded-[10px] gap-[15px] px-4 py-2.5 whitespace-nowrap"
                onClick={() => {
                  setIsOpen(true);

                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: "clickFilter",
                    tournamentName: tournamentName || "",
                  });
                }}
              >
                <div>
                  {appliedFiltersCount > 0 && (
                    <span>[ {appliedFiltersCount} ] </span>
                  )}
                  <span>Filter</span>
                </div>
                <div className="relative h-full">
                  <TuneIcon fontSize="medium" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <motion.div
          variants={backgroundVariants}
          animate={isOpen ? "open" : "initial"}
          onClick={() => setIsOpen(false)}
          transition={{ duration: 0.3 }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "black",
            zIndex: 1,
          }}
        />
        <motion.div
          id="filterPanel"
          className="fixed bottom-0 z-[5] w-full rounded-[20px_0_0_20px]
    bg-tm-c-gray-950 opacity-0 overflow-hidden h-[calc(100vh_-_100px)]
    lg:h-[100vh] lg:w-[475px] lg:top-0 lg:right-0"
          variants={filterPanelVariants}
          animate={isOpen ? "open" : "initial"}
          transition={{ duration: 0.4 }}
        >
          <div className="flex flex-col h-full px-[50px] py-[30px]">
            <div className="text-[17px] flex items-center justify-center">
              <div
                className="flex lg:hidden absolute right-auto cursor-pointer lg:right-[30px]"
                onClick={() => setIsOpen(false)}
              >
                <KeyboardArrowDownIcon fontSize="medium" />
              </div>

              <div className="hidden lg:flex">
                <span>Filter</span>
                <div
                  className="flex absolute right-auto cursor-pointer lg:right-[30px]"
                  onClick={() => setIsOpen(false)}
                >
                  <CloseIcon fontSize="medium" />
                </div>
              </div>
            </div>

            <div
              className="py-10 flex flex-col text-[17px] h-full flex-1 overflow-y-auto
    [scrollbar-width:none] [-ms-overflow-style:none] 2xl:py-[100px]"
            >
              <div>
                <span className="text-xs tracking-[0.36px] opacity-50">
                  Gender
                </span>
                <div className="flex flex-col gap-5 py-5 text-[15px]">
                  {GENDERS.map(
                    (option: { label: string; value: string | null }) => {
                      return (
                        <Checkbox
                          key={option?.value}
                          disabled={
                            tournamentQuery === "longestDrive" ||
                            // check if tournament startDate is AFTER current time
                            // as not all data from API is available before tournament start
                            dayjs(startDate).isAfter(dayjs())
                          }
                          label={option?.label}
                          value={option?.value}
                          isChecked={gender.value === option.value}
                          onChange={() => handleGenderChange(option)}
                        />
                      );
                    }
                  )}
                </div>
              </div>

              <div className="sticky top-0 mt-9 2xl:mt-14 mb-5">
                <span className="text-xs tracking-[0.36px] opacity-50">
                  Nationality
                </span>
              </div>
              <div className="pb-10 max-h-[calc(100vh-200px)] min-h-[200px] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] md:pb-20 2xl:pb-5">
                <div className="flex flex-col gap-5 pb-5 text-[15px]">
                  {nationalitiesOptions?.map(
                    (option: { label: string; value: string | null }) => {
                      return (
                        <Checkbox
                          key={option?.value}
                          label={option?.label}
                          value={option?.value}
                          isChecked={nationalities.some(
                            (n) => n.value === option.value
                          )}
                          onChange={handleNationalityChange}
                        />
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 flex items-center justify-center gap-2.5 h-[95px] lg:h-[120px] bg-[var(--colors-Items-secondary,#141414)] shadow-[0px_-25px_105px_10px_#090909] rounded-[10px]">
            <Cta variant="secondary" name="Clear" onClick={onClear} />
            <Cta name="Apply" onClick={onApply} />
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default LeaderboardActionBar;
